import React, { FC, useState } from 'react';
import { Avatar, Button, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { PhotoCamera } from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Delete';
import useUserMeQuery from '../../reactQuery/hooks/useUserMeQuery';
import { IUserProfileFields } from '../../lib/interfaces/User';
import { uploadFile } from '../../api/apis';
import { formatBytes } from '../../lib/helpers/generalFormatters';
import { vImageTypes } from '../Form/form.validations';
import './UserProfilePage.scss';

type UserAvatarProps = {
	onUserAvatarUpdate: (data: Partial<IUserProfileFields>) => void;
};

const UserAvatar: FC<UserAvatarProps> = ({ onUserAvatarUpdate }) => {
	const { t } = useTranslation();
	const { data: currentUser } = useUserMeQuery();
	const theme = useTheme();
	const [avatarError, setAvatarError] = useState<string>('');

	const validateAvatarFile = (file: File) => {
		if (file.size > 500000) {
			const errorMessage = t('maxSizeAvatarError', {
				actualSize: formatBytes(file.size),
				maxSize: '500 KB',
			});
			setAvatarError(errorMessage);
			return;
		}
		if (!vImageTypes(file)) {
			const allowedExtentions = 'PNG, JPG, JPEG';
			setAvatarError(t('imageTypeError', { allowedExtentions }));
			return;
		}
		setAvatarError('');
		return true;
	};

	const uploadPhoto = (file: File) => {
		const body = new FormData();

		if (validateAvatarFile(file)) {
			body.append('image', file);
			uploadFile(body).then(
				(res) => {
					onUserAvatarUpdate({ avatar: res.url });
				},
				(err) => {
					console.error(err);
				},
			);
		}
	};

	const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (!e.target.files) {
			return;
		}
		uploadPhoto(e.target.files[0]);
	};

	return (
		<div className="UserProfilePage__col">
			<div className="UserProfilePage__image">
				{currentUser?.avatar ? (
					<img src={currentUser.avatar} alt={currentUser?.firstName} />
				) : (
					<Avatar
						aria-label="recipe"
						sx={{
							width: '300px',
							height: '300px',
							fontSize: '8rem',
							background: theme.palette.secondary.main,
							borderRadius: '9px',
						}}
					>
						{currentUser?.firstName.split('')[0]}
					</Avatar>
				)}
				<Button startIcon={<PhotoCamera />} component="label">
					{t('editPhoto')}
					<input
						hidden
						accept="image/*"
						type="file"
						onChange={(e) => handleFileChange(e)}
					/>
				</Button>
				{currentUser?.avatar && (
					<Button
						startIcon={<DeleteIcon />}
						component="button"
						onClick={() => onUserAvatarUpdate({ avatar: null })}
					>
						{t('remove')}
					</Button>
				)}
				{avatarError && <div className="error-field">{avatarError}</div>}
			</div>
		</div>
	);
};

export default UserAvatar;
